import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Button, Table, Tag, Typography } from 'antd';
import { ArrowsAltOutlined, ShrinkOutlined } from '@ant-design/icons';

import { useAccess } from '../../../auth/AccessContext.jsx';
import folderIconUrl from '../../../assets/folder icon.svg';
import ActionIcon from '../../../components/ActionIcon.jsx';
import { formatMoney, formatUnits } from '../../../format';
import HolderDrawer from './HolderDrawer.jsx';
import { accountRows, ageHeading, holderRows, holdersQuery, holdersShown, rowTotals, sharePercent } from './register.js';
import { usePagedList } from './usePagedList.js';

const { Text } = Typography;

const units = (v) => (v === null || v === undefined ? '—' : formatUnits(v));
const money = (v) => (v === null || v === undefined ? '—' : formatMoney(v));

/** Units and amount of the rows the table is showing, above it. */
export const RegisterTableTotals = ({ units: unitsTotal, amount }) => (
    <div className="register-table-totals">
        <div className="register-table-total">
            <span className="register-table-total-label">Total units</span>
            {/* Rows of several funds have no units total: units of different funds do not add up. */}
            <span className="register-table-total-value">{unitsTotal === null || unitsTotal === undefined ? '—' : formatUnits(unitsTotal)}</span>
        </div>
        <div className="register-table-total">
            <span className="register-table-total-label">Total amount (LKR)</span>
            <span className="register-table-total-value">{amount === null || amount === undefined ? '—' : formatMoney(amount)}</span>
        </div>
    </div>
);

/** An account row's Holder cell: the account's name, its LIV account code, and how many strategies are under it. */
const AccountCell = ({ account, expanded, onToggle }) => (
    <div className="register-account-cell">
        <FolderExpand expanded={expanded} onExpand={onToggle} record={account} />
        <span className="register-holder-name register-account-name">{account.fullName}</span>
    </div>
);

/** Figma's folder control; its number is the account's strategy count. */
function FolderExpand({ expanded, onExpand, record }) {
    return (
        <button
            type="button"
            className={'register-folder-expand' + (expanded ? ' is-expanded' : '')}
            aria-label={(expanded ? 'Collapse ' : 'Expand ') + record.fullName}
            onClick={(event) => {
                event.stopPropagation();
                onExpand(record, event);
            }}
        >
            <span className="register-folder-icon">
                <img src={folderIconUrl} alt="" />
                <span>{record.strategies.length}</span>
            </span>
        </button>
    );
}

/** A strategy row's Holder cell: its strategy reference and its holder's name (the holder number is on the drawer). */
const StrategyCell = ({ holder }) => (
    <>
        {holder.strategyRef && <span className="register-strategy-ref">{holder.strategyRef}</span>}
        <span className="register-holder-name">{holder.fullName}</span>
        {holder.status && holder.status !== 'ACTIVE' && <Tag color="orange" style={{ marginLeft: 8 }}>{holder.status}</Tag>}
    </>
);

/**
 * One fund's holders as the table shows them (register.js holderRows), by
 * client account (IP-158, register.js accountRows): each account a row of
 * its strategies' sums - units, value at the latest NAV per unit, share of
 * the units in issue, cost, and the young and old units of the open lots by
 * the fund's exit-fee holding months today - that expands (its row, or the
 * arrow) to the same columns for each of its strategies. Expand all and
 * Collapse all open or close every account at once. A strategy opens its
 * holder's drawer. Totals of the strategies shown sit above the table.
 * Rendered by FundUnitAllocation, and on its own by UnitRegistry.test.js.
 */
export const HoldersTable = ({ rows, holdingMonths = null, asOf = null, loading = false, allFunds = false, onOpen }) => {
    const accounts = useMemo(() => accountRows(rows), [rows]);
    const [expanded, setExpanded] = useState([]);
    const allKeys = accounts.map((a) => a.key);
    const allOpen = allKeys.length > 0 && allKeys.every((k) => expanded.includes(k));
    const toggle = (key) => setExpanded((current) => (current.includes(key) ? current.filter((k) => k !== key) : [...current, key]));
    const columns = [
        {
            title: <span className="register-holder-heading">Holder</span>,
            key: 'holder',
            render: (_, r) => (r.strategies
                ? <AccountCell account={r} expanded={expanded.includes(r.key)} onToggle={() => toggle(r.key)} />
                : <StrategyCell holder={r} />)
        },
        // Every fund shown: which fund each row is (an account row lists the funds its strategies hold).
        ...(allFunds ? [{
            title: 'Fund',
            key: 'fund',
            width: 110,
            render: (_, r) => (r.strategies ? r.funds.join(', ') : r.fundId)
        }] : []),
        { title: 'Units', dataIndex: 'units', key: 'units', width: 120, render: units },
        { title: 'Value (LKR)', dataIndex: 'value', key: 'value', width: 120, render: money },
        {
            title: 'Share of Fund',
            dataIndex: 'share',
            key: 'share',
            width: 147,
            render: (v) => (v === null
                ? '—'
                : (
                    <div className="register-share">
                        <div className="register-share-rail"><div className="register-share-fill" style={{ width: sharePercent(v) + '%' }} /></div>
                        <span>{sharePercent(v)}%</span>
                    </div>
                ))
        },
        { title: 'Cost (LKR)', dataIndex: 'costValue', key: 'costValue', width: 120, render: money },
        {
            title: <span title={ageHeading(holdingMonths)}>Young Units</span>,
            dataIndex: 'youngUnits',
            key: 'youngUnits',
            width: 133,
            render: units
        },
        { title: 'Old Units', dataIndex: 'oldUnits', key: 'oldUnits', width: 120, render: units }
    ];
    return (
        <>
            <div className="register-info register-holders-info">
                <ActionIcon name="info" />
                <span>
                    Value is units x the latest published NAV per unit; share is of the units in issue. Young units are those
                    {' '}{ageHeading(holdingMonths)}{asOf ? ' on ' + asOf : ''} (an exit fee applies to them). a dash means the fund has no
                    fee configuration in force. Each client account adds up its strategies: expand it to see them, and select a
                    strategy to see its lots and history.
                </span>
            </div>
            <div className="register-holders-toolbar">
                <RegisterTableTotals {...rowTotals(rows)} />
                <Button className="register-toolbar-btn" icon={allOpen ? <ShrinkOutlined /> : <ArrowsAltOutlined />} disabled={!allKeys.length}
                    onClick={() => setExpanded(allOpen ? [] : allKeys)}>
                    {allOpen ? 'Collapse all' : 'Expand all'}
                </Button>
            </div>
            <Table
                rowKey={(r) => r.key ?? r.holderId}
                className="register-table register-holders-table"
                loading={loading}
                columns={columns}
                dataSource={accounts}
                pagination={false}
                locale={{ emptyText: allFunds ? 'No holder has units in any fund' : 'No holder has units in this fund' }}
                expandable={{
                    childrenColumnName: 'strategies',
                    expandedRowKeys: expanded,
                    onExpandedRowsChange: (keys) => setExpanded([...keys]),
                    indentSize: 20,
                    showExpandColumn: false
                }}
                rowClassName={(r) => (r.strategies ? 'register-account-row' : 'register-strategy-row')}
                onRow={(r) => ({ onClick: () => (r.strategies ? toggle(r.key) : onOpen(r)), style: { cursor: 'pointer' } })}
            />
        </>
    );
};

/**
 * Unit allocation for one fund (GET register/funds/{fund}/holders, a page of
 * client accounts at a time so an account's strategies arrive together
 * (IP-158); Load more for the next page), and the holder drawer. A holder's
 * transactions open on the Transactions view, filtered to them
 * (onHolderTransactions).
 */
const FundUnitAllocation = ({ fund, funds = [], onHolderTransactions }) => {
    const { api } = useAccess();
    const [ages, setAges] = useState({ holdingMonths: null, asOf: null });
    const [openHolder, setOpenHolder] = useState(null);
    const fundId = fund?.fundId ?? null;

    const fetchPage = useCallback(async (cursor) => {
        // A fund card chosen: that fund. None: every fund (as the download has them), each holder's funds side by side.
        const page = fundId
            ? await api.register.holders(fundId, holdersQuery({ cursor }))
            : await api.register.allocation(holdersQuery({ cursor }));
        if (!cursor) setAges({ holdingMonths: page.holdingMonths ?? null, asOf: page.asOf ?? null });
        // A server not yet paging answers { holders } alone: that is the whole list. rowKey names the holder and the
        // fund: with every fund shown, one holder is a row per fund, and Load more must keep each one once.
        return { items: (page.holders ?? []).map((h) => ({ ...h, rowKey: h.holderId + '|' + (h.fundId ?? fundId) })), nextCursor: page.nextCursor ?? null };
    }, [api, fundId]);
    const list = usePagedList(fetchPage, 'rowKey');

    return (
        <>
            {list.error && <Alert type="error" showIcon message={list.error} className="auth-alert" closable onClose={list.clearError} />}
            {!fund && <Text type="secondary" className="register-all-funds-note">Every fund&#39;s holders. Select a fund card to see one fund.</Text>}
            <HoldersTable rows={holderRows(list.items, fund, funds)} holdingMonths={ages.holdingMonths} asOf={ages.asOf} loading={list.loading}
                allFunds={!fund} onOpen={(h) => setOpenHolder(h)} />
            <div className="register-list-footer">
                <Text type="secondary">{holdersShown(list.items, fund?.holderCount ?? null)}</Text>
                {list.nextCursor && <Button onClick={list.loadMore} loading={list.loadingMore}>Load more</Button>}
            </div>
            <HolderDrawer
                holder={openHolder}
                onClose={() => setOpenHolder(null)}
                onTransactions={(h) => { setOpenHolder(null); onHolderTransactions(h); }}
            />
        </>
    );
};

export default FundUnitAllocation;
